import { useMutation } from "@tanstack/react-query";
import { Button, Callout } from "@/design/components";
import { openBillingPortal, goToBillingPortal } from "@/api/billing";
import { ApiError } from "@/api/client";
import styles from "./DunningBanner.module.css";

const COPY = {
  past_due: {
    tone: "warning" as const,
    title: "Payment past due",
    body: "We couldn't take your latest payment. Update your card to avoid losing access — your plan stays active while Stripe retries.",
  },
  unpaid: {
    tone: "danger" as const,
    title: "Subscription unpaid — downgraded to Free",
    body: "Payment retries were exhausted, so your org is on the Free plan. Update your billing to restore your paid quota.",
  },
};

/**
 * Dunning alert shown on the Dashboard + Billing when the Stripe subscription is
 * past_due (grace) or unpaid (downgraded). Self-contained: owns the Billing Portal
 * call so any screen can drop it in with just the status. Renders nothing for any
 * other status (incl. null/active).
 */
export function DunningBanner({ status }: { status: string | null }) {
  const portal = useMutation({
    mutationFn: () => openBillingPortal(),
    onSuccess: (url) => goToBillingPortal(url), // leaves the SPA for the Stripe-hosted portal
  });

  if (status !== "past_due" && status !== "unpaid") return null;
  const c = COPY[status];

  return (
    <Callout tone={c.tone} title={c.title}>
      {c.body}
      <div className={styles.actions}>
        <Button size="sm" loading={portal.isPending} onClick={() => portal.mutate()}>
          Manage billing
        </Button>
        {portal.isError && (
          <span className={styles.err} role="alert">
            {portal.error instanceof ApiError ? portal.error.message : "Couldn't open billing — please try again."}
          </span>
        )}
      </div>
    </Callout>
  );
}
